export interface FileTypeInfo {
    category: 'text' | 'document' | 'spreadsheet' | 'pdf' | 'image' | 'audio' | 'video' | 'binary' | 'code' | 'data'
    parser: 'text' | 'pdf' | 'office' | 'csv' | 'json' | 'xml' | 'binary' | 'image' | 'none' | 'llamaparse'
    mimeTypes: string[]
    description: string
}

export const FILE_TYPE_MAP: Record<string, FileTypeInfo> = {
    // Text files
    'txt': { category: 'text', parser: 'text', mimeTypes: ['text/plain'], description: 'Plain text' },
    'md': { category: 'text', parser: 'text', mimeTypes: ['text/markdown'], description: 'Markdown' },
    'rtf': { category: 'text', parser: 'text', mimeTypes: ['application/rtf'], description: 'Rich text format' },
    'log': { category: 'text', parser: 'text', mimeTypes: ['text/plain'], description: 'Log file' },
    'readme': { category: 'text', parser: 'text', mimeTypes: ['text/plain'], description: 'README file' },
    
    // Code files
    'js': { category: 'code', parser: 'text', mimeTypes: ['application/javascript'], description: 'JavaScript' },
    'jsx': { category: 'code', parser: 'text', mimeTypes: ['application/javascript'], description: 'JSX' },
    'ts': { category: 'code', parser: 'text', mimeTypes: ['application/typescript'], description: 'TypeScript' },
    'tsx': { category: 'code', parser: 'text', mimeTypes: ['application/typescript'], description: 'TSX' },
    'py': { category: 'code', parser: 'text', mimeTypes: ['text/x-python'], description: 'Python' },
    'java': { category: 'code', parser: 'text', mimeTypes: ['text/x-java-source'], description: 'Java' },
    'c': { category: 'code', parser: 'text', mimeTypes: ['text/x-c'], description: 'C' },
    'cpp': { category: 'code', parser: 'text', mimeTypes: ['text/x-c++'], description: 'C++' },
    'h': { category: 'code', parser: 'text', mimeTypes: ['text/x-c'], description: 'C header' },
    'hpp': { category: 'code', parser: 'text', mimeTypes: ['text/x-c++'], description: 'C++ header' },
    'cs': { category: 'code', parser: 'text', mimeTypes: ['text/x-csharp'], description: 'C#' },
    'php': { category: 'code', parser: 'text', mimeTypes: ['application/x-httpd-php'], description: 'PHP' },
    'rb': { category: 'code', parser: 'text', mimeTypes: ['application/x-ruby'], description: 'Ruby' },
    'go': { category: 'code', parser: 'text', mimeTypes: ['text/x-go'], description: 'Go' },
    'rs': { category: 'code', parser: 'text', mimeTypes: ['text/x-rust'], description: 'Rust' },
    'swift': { category: 'code', parser: 'text', mimeTypes: ['text/x-swift'], description: 'Swift' },
    'kt': { category: 'code', parser: 'text', mimeTypes: ['text/x-kotlin'], description: 'Kotlin' },
    'scala': { category: 'code', parser: 'text', mimeTypes: ['text/x-scala'], description: 'Scala' },
    'r': { category: 'code', parser: 'text', mimeTypes: ['text/x-r'], description: 'R' },
    'sql': { category: 'code', parser: 'text', mimeTypes: ['application/sql'], description: 'SQL' },
    'sh': { category: 'code', parser: 'text', mimeTypes: ['application/x-sh'], description: 'Shell script' },
    'bat': { category: 'code', parser: 'text', mimeTypes: ['application/x-bat'], description: 'Batch file' },
    'ps1': { category: 'code', parser: 'text', mimeTypes: ['application/x-powershell'], description: 'PowerShell' },
    
    // Markup and config files
    'html': { category: 'text', parser: 'text', mimeTypes: ['text/html'], description: 'HTML' },
    'htm': { category: 'text', parser: 'text', mimeTypes: ['text/html'], description: 'HTML' },
    'css': { category: 'text', parser: 'text', mimeTypes: ['text/css'], description: 'CSS' },
    'scss': { category: 'text', parser: 'text', mimeTypes: ['text/x-scss'], description: 'SCSS' },
    'sass': { category: 'text', parser: 'text', mimeTypes: ['text/x-sass'], description: 'Sass' },
    'less': { category: 'text', parser: 'text', mimeTypes: ['text/x-less'], description: 'Less' },
    'xml': { category: 'data', parser: 'xml', mimeTypes: ['application/xml', 'text/xml'], description: 'XML' },
    'yaml': { category: 'data', parser: 'text', mimeTypes: ['application/x-yaml'], description: 'YAML' },
    'yml': { category: 'data', parser: 'text', mimeTypes: ['application/x-yaml'], description: 'YAML' },
    'toml': { category: 'data', parser: 'text', mimeTypes: ['application/toml'], description: 'TOML' },
    'ini': { category: 'data', parser: 'text', mimeTypes: ['text/plain'], description: 'INI config' },
    'conf': { category: 'data', parser: 'text', mimeTypes: ['text/plain'], description: 'Configuration' },
    'env': { category: 'data', parser: 'text', mimeTypes: ['text/plain'], description: 'Environment variables' },
    'gitignore': { category: 'data', parser: 'text', mimeTypes: ['text/plain'], description: 'Git ignore' },
    'dockerfile': { category: 'data', parser: 'text', mimeTypes: ['text/plain'], description: 'Dockerfile' },
    
    // Data files
    'json': { category: 'data', parser: 'json', mimeTypes: ['application/json'], description: 'JSON' },
    'csv': { category: 'data', parser: 'csv', mimeTypes: ['text/csv'], description: 'CSV' },
    'tsv': { category: 'data', parser: 'csv', mimeTypes: ['text/tab-separated-values'], description: 'TSV' },
    'jsonl': { category: 'data', parser: 'text', mimeTypes: ['application/jsonl'], description: 'JSON Lines' },
    'ndjson': { category: 'data', parser: 'text', mimeTypes: ['application/x-ndjson'], description: 'Newline delimited JSON' },
    
    // PDF
    'pdf': { category: 'pdf', parser: 'pdf', mimeTypes: ['application/pdf'], description: 'PDF document' },
    
    // Microsoft Office
    'docx': { category: 'document', parser: 'office', mimeTypes: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'], description: 'Word document' },
    'doc': { category: 'document', parser: 'llamaparse', mimeTypes: ['application/msword'], description: 'Legacy Word document' },
    'xlsx': { category: 'spreadsheet', parser: 'office', mimeTypes: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'], description: 'Excel spreadsheet' },
    'xls': { category: 'spreadsheet', parser: 'llamaparse', mimeTypes: ['application/vnd.ms-excel'], description: 'Legacy Excel spreadsheet' },
    'pptx': { category: 'document', parser: 'office', mimeTypes: ['application/vnd.openxmlformats-officedocument.presentationml.presentation'], description: 'PowerPoint presentation' },
    'ppt': { category: 'document', parser: 'llamaparse', mimeTypes: ['application/vnd.ms-powerpoint'], description: 'Legacy PowerPoint presentation' },
    
    // OpenDocument formats
    'odt': { category: 'document', parser: 'llamaparse', mimeTypes: ['application/vnd.oasis.opendocument.text'], description: 'OpenDocument text' },
    'ods': { category: 'spreadsheet', parser: 'llamaparse', mimeTypes: ['application/vnd.oasis.opendocument.spreadsheet'], description: 'OpenDocument spreadsheet' },
    'odp': { category: 'document', parser: 'llamaparse', mimeTypes: ['application/vnd.oasis.opendocument.presentation'], description: 'OpenDocument presentation' },
    
    // Images (for future OCR potential, but no text extraction for now)
    'jpg': { category: 'image', parser: 'none', mimeTypes: ['image/jpeg'], description: 'JPEG image' },
    'jpeg': { category: 'image', parser: 'none', mimeTypes: ['image/jpeg'], description: 'JPEG image' },
    'png': { category: 'image', parser: 'none', mimeTypes: ['image/png'], description: 'PNG image' },
    'gif': { category: 'image', parser: 'none', mimeTypes: ['image/gif'], description: 'GIF image' },
    'webp': { category: 'image', parser: 'none', mimeTypes: ['image/webp'], description: 'WebP image' },
    'svg': { category: 'image', parser: 'text', mimeTypes: ['image/svg+xml'], description: 'SVG image' },
    'bmp': { category: 'image', parser: 'none', mimeTypes: ['image/bmp'], description: 'BMP image' },
    'tiff': { category: 'image', parser: 'none', mimeTypes: ['image/tiff'], description: 'TIFF image' },
    'tif': { category: 'image', parser: 'none', mimeTypes: ['image/tiff'], description: 'TIFF image' },
    'heic': { category: 'image', parser: 'none', mimeTypes: ['image/heic'], description: 'HEIC image (iPhone)' },
    'heif': { category: 'image', parser: 'none', mimeTypes: ['image/heif'], description: 'HEIF image' },
    
    // Audio (no text content to extract)
    'mp3': { category: 'audio', parser: 'none', mimeTypes: ['audio/mpeg'], description: 'MP3 audio' },
    'wav': { category: 'audio', parser: 'none', mimeTypes: ['audio/wav'], description: 'WAV audio' },
    'flac': { category: 'audio', parser: 'none', mimeTypes: ['audio/flac'], description: 'FLAC audio' },
    'ogg': { category: 'audio', parser: 'none', mimeTypes: ['audio/ogg'], description: 'OGG audio' },
    'm4a': { category: 'audio', parser: 'none', mimeTypes: ['audio/mp4'], description: 'M4A audio' },
    'aac': { category: 'audio', parser: 'none', mimeTypes: ['audio/aac'], description: 'AAC audio' },
    
    // Video (no text content to extract)
    'mp4': { category: 'video', parser: 'none', mimeTypes: ['video/mp4'], description: 'MP4 video' },
    'avi': { category: 'video', parser: 'none', mimeTypes: ['video/x-msvideo'], description: 'AVI video' },
    'mov': { category: 'video', parser: 'none', mimeTypes: ['video/quicktime'], description: 'QuickTime video' },
    'wmv': { category: 'video', parser: 'none', mimeTypes: ['video/x-ms-wmv'], description: 'WMV video' },
    'flv': { category: 'video', parser: 'none', mimeTypes: ['video/x-flv'], description: 'FLV video' },
    'webm': { category: 'video', parser: 'none', mimeTypes: ['video/webm'], description: 'WebM video' },
    'mkv': { category: 'video', parser: 'none', mimeTypes: ['video/x-matroska'], description: 'MKV video' },
    
    // TeX/LaTeX
    'tex': { category: 'text', parser: 'text', mimeTypes: ['application/x-tex'], description: 'LaTeX document' },
    'bib': { category: 'text', parser: 'text', mimeTypes: ['application/x-bibtex'], description: 'BibTeX bibliography' },
    
    // Other document formats
    'epub': { category: 'document', parser: 'llamaparse', mimeTypes: ['application/epub+zip'], description: 'EPUB ebook' },
    'mobi': { category: 'document', parser: 'llamaparse', mimeTypes: ['application/x-mobipocket-ebook'], description: 'Kindle ebook' },
    
    // Special files
    'patch': { category: 'text', parser: 'text', mimeTypes: ['text/x-patch'], description: 'Patch file' },
    'diff': { category: 'text', parser: 'text', mimeTypes: ['text/x-diff'], description: 'Diff file' }
}

export function getFileTypeInfo(extension: string): FileTypeInfo | null {
    const normalizedExt = extension.toLowerCase().replace(/^\./, '')
    return FILE_TYPE_MAP[normalizedExt] || null
}

export function detectFileType(filename: string): FileTypeInfo | null {
    const extension = filename.split('.').pop()
    if (!extension) return null
    return getFileTypeInfo(extension)
}

export function isTextFile(extension: string): boolean {
    const info = getFileTypeInfo(extension)
    return info?.category === 'text' || info?.category === 'code' || info?.parser === 'text'
}

export function isParsableFile(extension: string): boolean {
    const info = getFileTypeInfo(extension)
    return info !== null && info.parser !== 'none'
}

export function shouldUseLlamaParse(extension: string): boolean {
    const info = getFileTypeInfo(extension)
    return info?.parser === 'llamaparse'
}

export const SUPPORTED_EXTENSIONS = Object.keys(FILE_TYPE_MAP)

export const PARSABLE_EXTENSIONS = SUPPORTED_EXTENSIONS.filter(ext => {
    const info = FILE_TYPE_MAP[ext]
    return info.parser !== 'none'
}) 